import { useState, useCallback, useRef } from 'react';
import { useMailApi } from '../mail/api-context';
import { useMailModel } from './use-mail-model';
import {
  categorizeFolderItems,
  type CategorizeFolderResult,
} from '../services/mail-batch-categorizer';

export type { CategorizeFolderResult };

interface State {
  running: boolean;
  progress: { categorized: number; target: number } | null;
  lastActivityAt: number | null;
  result: CategorizeFolderResult | null;
  error: string | null;
}

export function useCategorizeFolder() {
  const api = useMailApi();
  const { suggest } = useMailModel();
  const cancelRef = useRef(false);
  const [state, setState] = useState<State>({
    running: false, progress: null, lastActivityAt: null, result: null, error: null,
  });

  const run = useCallback(async (accountId: string, folder: string) => {
    cancelRef.current = false;
    setState({ running: true, progress: null, lastActivityAt: Date.now(), result: null, error: null });
    try {
      const result = await categorizeFolderItems(
        api,
        suggest,
        accountId,
        folder,
        cancelRef,
        (categorized, checked) =>
          setState((s) => ({ ...s, progress: { categorized, target: checked }, lastActivityAt: Date.now() })),
      );
      setState({ running: false, progress: null, lastActivityAt: null, result, error: null });
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fehler beim Kategorisieren';
      setState({ running: false, progress: null, lastActivityAt: null, result: null, error: msg });
      return null;
    }
  }, [api, suggest]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    setState((s) => ({ ...s, running: false, progress: null, lastActivityAt: null }));
  }, []);

  return { run, cancel, ...state };
}
